/**
 * Indicative Azure pricing for the admin cost previews.
 *
 * Every rate below was read from the Azure Retail Prices API for `westus` in
 * USD, which is the region this deployment runs in. They are list prices: they
 * ignore reservations, savings plans, dev/test rates, and any EA or MCA
 * discount, so treat them as an order-of-magnitude planning aid. Azure Cost
 * Management remains the source of truth for what was actually billed.
 *
 *   az rest --url "https://prices.azure.com/api/retail/prices?\$filter=..."
 */

export const PRICING_META = {
  region: 'westus',
  regionLabel: 'West US',
  currency: 'USD',
  source: 'Azure Retail Prices API (list prices)',
  capturedOn: '2026-08-31',
} as const;

/**
 * Azure SQL Database, Single database, General Purpose, provisioned compute,
 * Gen5. Billed per vCore-hour; storage and backup are billed separately and
 * are not modelled here because scaling vCores does not change them.
 */
export const SQL_VCORE_HOUR = 0.17;

/**
 * Container Apps on the Consumption workload profile. Billed per resource-second,
 * with a lower idle rate that applies when a replica is provisioned but not
 * actively serving. Both the Reader and the nightly job run on Consumption.
 *
 * There is also a monthly free grant (180,000 vCPU-seconds and 360,000
 * GiB-seconds). It is deliberately not netted off below: it is consumed by
 * whatever runs first in the month, so subtracting it from a single run's cost
 * would overstate the saving.
 */
export const CONTAINER_APPS = {
  vCpuActiveSecond: 0.000024,
  vCpuIdleSecond: 0.000003,
  memoryGibSecond: 0.000003,
  requestsPerMillion: 0.4,
  freeGrantVCpuSeconds: 180_000,
  freeGrantGibSeconds: 360_000,
} as const;

export interface ReplicaSpec {
  vCpu: number;
  memoryGib: number;
}

/** Reader replica sizing from `scripts/deployment/reader-oss.bicep`. */
export const READER_REPLICA: ReplicaSpec = { vCpu: 2, memoryGib: 4 };

/** The nightly job's own runner container. */
export const NIGHTLY_JOB_REPLICA: ReplicaSpec = { vCpu: 0.5, memoryGib: 1 };

/** vCores the nightly run scales SQL between. */
export const SQL_NIGHTLY_VCORES = 8;
export const SQL_IDLE_VCORES = 2;

/**
 * App Service plan list prices per instance, USD, Linux, West US.
 *
 * This is the single source for App Service rates; `adminApi` builds its
 * richer `SkuSpec` records from it rather than carrying its own copy. Keeping
 * the numbers here means a page can price a run without importing the API
 * client, which also keeps them available when that client is mocked in tests.
 */
export const APP_SERVICE_SKU_RATES: Record<string, { costPerHour: number; costPerMonth: number }> = {
  B1: { costPerHour: 0.018, costPerMonth: 13.14 },
  B2: { costPerHour: 0.035, costPerMonth: 25.55 },
  B3: { costPerHour: 0.07, costPerMonth: 51.1 },
  P0v3: { costPerHour: 0.085, costPerMonth: 62.05 },
  P1v3: { costPerHour: 0.17, costPerMonth: 124.1 },
  P2v3: { costPerHour: 0.34, costPerMonth: 248.2 },
  P3v3: { costPerHour: 0.68, costPerMonth: 496.4 },
};

/**
 * Hourly rate for an App Service SKU name as ARM reports it.
 *
 * ARM's casing is not stable (`P1v3` and `P1V3` both occur), so the lookup is
 * case-insensitive. Returns null for an unrecognised SKU so callers can show
 * "unknown" rather than silently pricing it at zero.
 */
export function appServiceSkuCostPerHour(sku: string | null | undefined): number | null {
  if (!sku) return null;
  const key = Object.keys(APP_SERVICE_SKU_RATES).find(
    candidate => candidate.toUpperCase() === String(sku).toUpperCase(),
  );
  return key ? APP_SERVICE_SKU_RATES[key].costPerHour : null;
}

const SECONDS_PER_HOUR = 3600;

/** Hourly cost of one Consumption-profile replica of the given size. */
export function replicaHourlyCost(spec: ReplicaSpec, idle = false): number {
  const vCpuRate = idle ? CONTAINER_APPS.vCpuIdleSecond : CONTAINER_APPS.vCpuActiveSecond;
  return (spec.vCpu * vCpuRate + spec.memoryGib * CONTAINER_APPS.memoryGibSecond) * SECONDS_PER_HOUR;
}

/** Hourly cost of `replicas` Reader replicas. */
export function readerHourlyCost(replicas: number, idle = false): number {
  return Math.max(0, replicas) * replicaHourlyCost(READER_REPLICA, idle);
}

/** Hourly cost of an Azure SQL database at `vCores`. */
export function sqlHourlyCost(vCores: number): number {
  return Math.max(0, vCores) * SQL_VCORE_HOUR;
}

/** Hourly cost of an App Service plan: per-instance SKU rate times instances. */
export function functionHourlyCost(skuCostPerHour: number, instances: number): number {
  return Math.max(0, skuCostPerHour) * Math.max(0, instances);
}

export type CostComponentId = 'functions' | 'sql' | 'reader' | 'job';

export interface CostComponent {
  id: CostComponentId;
  label: string;
  /** Human-readable description of the scaled configuration. */
  configuration: string;
  /** Human-readable description of the at-rest configuration. */
  baselineConfiguration: string;
  hourlyCost: number;
  baselineHourlyCost: number;
  runCost: number;
  baselineRunCost: number;
  /** Cost that would not have been incurred had the run not happened. */
  incrementalRunCost: number;
}

export interface NightlyCostInputs {
  minutes: number;
  functionSku: string;
  functionSkuCostPerHour: number;
  functionInstances: number;
  sqlVCores: number;
  readerReplicas: number;
  baselineFunctionSku: string;
  baselineFunctionSkuCostPerHour: number;
  baselineFunctionInstances: number;
  baselineSqlVCores: number;
}

export interface NightlyCostBreakdown {
  minutes: number;
  components: CostComponent[];
  totalHourlyCost: number;
  baselineHourlyCost: number;
  totalRunCost: number;
  baselineRunCost: number;
  incrementalRunCost: number;
}

/**
 * Cost of holding a given scaled configuration for `minutes`.
 *
 * Reports three numbers per component because they answer different questions:
 * `runCost` is what the window costs outright, `baselineRunCost` is what the
 * same window would have cost with everything at rest, and
 * `incrementalRunCost` is the difference -- the only figure attributable to
 * the run, since the at-rest platform is paid for around the clock regardless.
 */
export function nightlyRunCost(input: NightlyCostInputs): NightlyCostBreakdown {
  const minutes = Math.max(0, input.minutes);
  const hours = minutes / 60;

  const build = (
    id: CostComponentId,
    label: string,
    configuration: string,
    baselineConfiguration: string,
    hourlyCost: number,
    baselineHourlyCost: number,
  ): CostComponent => {
    const runCost = hourlyCost * hours;
    const baselineRunCost = baselineHourlyCost * hours;
    return {
      id,
      label,
      configuration,
      baselineConfiguration,
      hourlyCost,
      baselineHourlyCost,
      runCost,
      baselineRunCost,
      incrementalRunCost: runCost - baselineRunCost,
    };
  };

  const components: CostComponent[] = [
    build(
      'functions',
      'Azure Functions',
      `${input.functionSku} × ${input.functionInstances}`,
      `${input.baselineFunctionSku} × ${input.baselineFunctionInstances}`,
      functionHourlyCost(input.functionSkuCostPerHour, input.functionInstances),
      functionHourlyCost(input.baselineFunctionSkuCostPerHour, input.baselineFunctionInstances),
    ),
    build(
      'sql',
      'Azure SQL',
      `${input.sqlVCores} vCore${input.sqlVCores === 1 ? '' : 's'}`,
      `${input.baselineSqlVCores} vCore${input.baselineSqlVCores === 1 ? '' : 's'}`,
      sqlHourlyCost(input.sqlVCores),
      sqlHourlyCost(input.baselineSqlVCores),
    ),
    build(
      'reader',
      'Reader API',
      `${input.readerReplicas} replica${input.readerReplicas === 1 ? '' : 's'} × ${READER_REPLICA.vCpu} vCPU / ${READER_REPLICA.memoryGib} GiB`,
      'scaled to zero',
      readerHourlyCost(input.readerReplicas),
      0,
    ),
    build(
      'job',
      'Nightly job runner',
      `1 replica × ${NIGHTLY_JOB_REPLICA.vCpu} vCPU / ${NIGHTLY_JOB_REPLICA.memoryGib} GiB`,
      'not running',
      replicaHourlyCost(NIGHTLY_JOB_REPLICA),
      0,
    ),
  ];

  const sum = (fn: (c: CostComponent) => number) => components.reduce((total, c) => total + fn(c), 0);

  return {
    minutes,
    components,
    totalHourlyCost: sum(c => c.hourlyCost),
    baselineHourlyCost: sum(c => c.baselineHourlyCost),
    totalRunCost: sum(c => c.runCost),
    baselineRunCost: sum(c => c.baselineRunCost),
    incrementalRunCost: sum(c => c.incrementalRunCost),
  };
}

/**
 * One observation of what the platform was running at a point in a nightly run.
 * Mirrors the fields a `NightlyRunSamples` row carries.
 */
export interface CostSample {
  sampledAt?: string | null;
  elapsedSeconds?: number | null;
  functionSku?: string | null;
  functionInstanceCount?: number | null;
  sqlVCores?: number | null;
  readerReplicas?: number | null;
}

export interface ObservedCost {
  /** Total cost across every component for the covered window. */
  totalCost: number;
  /** Cost above what an at-rest platform would have cost over the same window. */
  incrementalCost: number;
  byComponent: Record<CostComponentId, number>;
  /** Seconds of the run that samples actually account for. */
  coveredSeconds: number;
  /** True when samples cover materially less than the run, so the total is a floor. */
  partial: boolean;
  sampleCount: number;
}

export interface ObservedCostOptions {
  /** Total run length, used to detect and report incomplete sample coverage. */
  runSeconds?: number | null;
  /** Resolves an App Service SKU name to its hourly rate. */
  skuCostPerHour: (sku: string | null | undefined) => number | null;
  baselineFunctionSkuCostPerHour: number;
  baselineFunctionInstances: number;
  baselineSqlVCores: number;
}

/**
 * Estimate what a run actually cost by integrating its samples.
 *
 * Each sample is treated as representative of the interval that ends at the
 * next sample, which is the correct reading for a periodic snapshot: it is the
 * only interpretation that does not require assuming what happened between two
 * observations. The final sample carries to the end of the run when the run
 * length is known, and is otherwise given the median interval so one trailing
 * sample cannot silently extend the estimate.
 *
 * Returns `partial: true` when the samples do not span the run, because in that
 * case the figure is a floor rather than an estimate -- which is exactly the
 * situation an unsampled phase creates.
 */
export function observedRunCost(samples: CostSample[], options: ObservedCostOptions): ObservedCost {
  const byComponent: Record<CostComponentId, number> = { functions: 0, sql: 0, reader: 0, job: 0 };
  const empty: ObservedCost = {
    totalCost: 0,
    incrementalCost: 0,
    byComponent,
    coveredSeconds: 0,
    partial: true,
    sampleCount: 0,
  };
  if (!Array.isArray(samples) || samples.length === 0) return empty;

  const points = samples
    .map(sample => ({
      sample,
      at: toSeconds(sample),
    }))
    .filter((p): p is { sample: CostSample; at: number } => p.at != null)
    .sort((a, b) => a.at - b.at);
  if (points.length === 0) return empty;

  const gaps: number[] = [];
  for (let i = 1; i < points.length; i += 1) gaps.push(points[i].at - points[i - 1].at);
  const medianGap = gaps.length > 0 ? [...gaps].sort((a, b) => a - b)[Math.floor(gaps.length / 2)] : 600;

  const runSeconds = options.runSeconds != null && options.runSeconds > 0 ? options.runSeconds : null;
  let coveredSeconds = 0;
  let incrementalCost = 0;

  points.forEach((point, index) => {
    const next = points[index + 1];
    let seconds: number;
    if (next) {
      seconds = next.at - point.at;
    } else if (runSeconds != null && runSeconds > point.at) {
      seconds = runSeconds - point.at;
    } else {
      seconds = medianGap;
    }
    if (!(seconds > 0)) return;
    const hours = seconds / 3600;
    coveredSeconds += seconds;

    const skuRate = options.skuCostPerHour(point.sample.functionSku) ?? 0;
    const instances = numberOr(point.sample.functionInstanceCount, 0);
    const vCores = numberOr(point.sample.sqlVCores, 0);
    const replicas = numberOr(point.sample.readerReplicas, 0);

    const functions = functionHourlyCost(skuRate, instances) * hours;
    const sql = sqlHourlyCost(vCores) * hours;
    const reader = readerHourlyCost(replicas) * hours;
    const job = replicaHourlyCost(NIGHTLY_JOB_REPLICA) * hours;

    byComponent.functions += functions;
    byComponent.sql += sql;
    byComponent.reader += reader;
    byComponent.job += job;

    const baseline =
      (functionHourlyCost(options.baselineFunctionSkuCostPerHour, options.baselineFunctionInstances) +
        sqlHourlyCost(options.baselineSqlVCores)) *
      hours;
    incrementalCost += functions + sql + reader + job - baseline;
  });

  const totalCost = byComponent.functions + byComponent.sql + byComponent.reader + byComponent.job;
  // Anything under 90% of the run leaves a phase unaccounted for; report it
  // rather than presenting a floor as though it were the whole cost.
  const partial = runSeconds != null ? coveredSeconds < runSeconds * 0.9 : true;

  return {
    totalCost,
    incrementalCost,
    byComponent,
    coveredSeconds,
    partial,
    sampleCount: points.length,
  };
}

function toSeconds(sample: CostSample): number | null {
  if (typeof sample.elapsedSeconds === 'number' && Number.isFinite(sample.elapsedSeconds)) {
    return sample.elapsedSeconds;
  }
  return null;
}

function numberOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
